import {
  Anchor,
  Badge,
  Box,
  Flex,
  Input,
  MultiSelect,
  Text,
  useMantineColorScheme,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { Modal } from "../Modal";
import classes from "./SearchModal.module.css";

import { IconSearch } from "@/components/icons";
import { paths } from "@/config/paths";
import { useMarketDecades } from "@/services/market/get-decades";
import { useMarketMakes } from "@/services/market/get-makes";
import { useSearchMarketCars } from "@/services/market/search-market-cars";
import { MarketCar } from "@/types/market";

import type { FC } from "react";

interface SearchModalProps {
  opened: boolean;
  open: () => void;
  close: () => void;
}

const SearchModal: FC<SearchModalProps> = ({ opened, close }) => {
  const { t } = useTranslation();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const inputRef = useRef<HTMLInputElement>(null);

  const { data: makes = [], isPending: isMakesLoading } = useMarketMakes();

  const makeNameMap = useMemo(() => {
    const map = new Map<string, string>();
    makes.forEach((make) => {
      map.set(make.id, make.name);
    });
    return map;
  }, [makes]);

  const [searchTerm, setSearchTerm] = useState("");
  const [debounced] = useDebouncedValue(searchTerm, 400);
  const [makeFilter, setMakeFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [yearFilter, setYearFilter] = useState<string[]>([]);
  const [filteredResults, setFilteredResults] = useState<MarketCar[]>([]);
  const [filtersVisible, setFiltersVisible] = useState<boolean>(false);
  const { data: decades, isPending: isDecadesLoading } = useMarketDecades();

  const isDisabled =
    (debounced.length === 0 && makeFilter.length === 0 && yearFilter.length === 0) ||
    isMakesLoading ||
    isDecadesLoading;

  const decadeOptions = useMemo(() => {
    if (!decades?.length) return [];

    return decades.map((decade) => {
      const match = decade.match(/(\d{4})s/);
      if (!match) return { value: decade, label: decade };
      const startYear = parseInt(match[1], 10);
      if (startYear === 9999) {
        return {
          value: decade,
          label: t("market.tabs.9999s"), // "Ongoing" cars
        };
      }
      const endYear = startYear + 9;
      return {
        value: decade,
        label: `${startYear} - ${endYear}`,
      };
    });
  }, [decades, t]);

  const {
    mutate: searchCars,
    data: searchResults,
    isPending,
    error,
    reset,
  } = useSearchMarketCars();

  const filterByDecade = (cars: MarketCar[], selectedDecades: string[]) => {
    if (!selectedDecades.length) return cars;

    const getProductionStartYear = (productionYears: string) => {
      const matchProduction = productionYears.match(/^(\d{4})/);
      if (!matchProduction) return undefined;
      const year = parseInt(matchProduction[1], 10);
      return Number.isNaN(year) ? undefined : year;
    };

    return cars.filter((car) => {
      const carYear = getProductionStartYear(car.production_years);
      if (carYear === undefined) return false;

      const matched = selectedDecades.some((decadeRaw) => {
        const decade = (decadeRaw || "").trim();

        if (decade === "pre-war") {
          const result = carYear < 1950;
          return result;
        }

        // Accept formats like "1950s" or short-form "50s"
        // Try full-year decade first (e.g. 1950s)
        const fullMatch = decade.match(/^(\d{4})s$/);
        if (fullMatch) {
          const start = parseInt(fullMatch[1], 10);
          const end = start + 9;
          const result = carYear >= start && carYear <= end;
          return result;
        }

        // Try short form like "50s" -> assume 1900s (1950s)
        const shortMatch = decade.match(/^(\d{2})s$/);
        if (shortMatch) {
          const two = parseInt(shortMatch[1], 10);
          const start = 1900 + two;
          const end = start + 9;
          const result = carYear >= start && carYear <= end;
          return result;
        }

        return false;
      });

      return matched;
    });
  };

  useEffect(() => {
    if (opened) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [opened]);

  // Updates the filtered results whenever search results, filters, or search term change
  useEffect(() => {
    if (!searchResults?.results) {
      setFilteredResults([]);
      return;
    }

    // Show filters when results arrive (instead of requiring click)
    setFiltersVisible(true);

    // Start from server results. Server already performs the full_text search,
    // so avoid re-applying a strict client-side text filter which can exclude
    // items the backend returned (causing empty UI results despite network hits).
    let results = [...searchResults.results];
    if (makeFilter.length) {
      results = results.filter((car) => makeFilter.includes(car.make_id));
    }
    if (yearFilter.length > 0) {
      results = filterByDecade(results, yearFilter);
    }

    setFilteredResults(results);
  }, [searchResults, debounced, makeFilter, statusFilter, yearFilter, makeNameMap]);

  // Handles search input changes: triggers API search only when there's text
  useEffect(() => {
    if (debounced.trim().length === 0) {
      reset();
      return;
    }

    // Ensure filters appear once the user has triggered a search
    setFiltersVisible(true);
    searchCars({ data: { full_text: debounced } });
  }, [debounced, searchCars, reset]);

  return (
    <Modal
      opened={opened}
      size="lg"
      onClose={() => {
        close();
        setSearchTerm("");
        setMakeFilter([]);
        setStatusFilter(null);
        setYearFilter([]);
        setFilteredResults([]);
        setFiltersVisible(false);
        reset();
      }}
      title={t("common.search")}
      icon={IconSearch}
    >
      <Input
        ref={inputRef}
        placeholder={t("header.search-placeholder")}
        value={searchTerm}
        onClick={() => !filtersVisible && setFiltersVisible(true)}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setFilteredResults([]);
        }}
        leftSection={<IconSearch size={16} />}
        mt={"sm"}
        mb="md"
      />

      {filtersVisible && (
        <Flex mt="lg" w="100%" direction={{ base: "column", sm: "row" }} justify="space-between">
          <Box w="3.75em">
            <Text size="sm">Filter by:</Text>
          </Box>
          <Flex justify="end" gap="sm">
            <MultiSelect
              data={makes.map((make) => ({ value: make.id, label: make.name }))}
              value={makeFilter}
              onChange={setMakeFilter}
              clearable
              searchable
              disabled={isDisabled}
              c={isDark ? "white" : "black"}
              w="150px"
              leftSection={
                makeFilter.length > 0 ? (
                  <Badge variant="light" fw={500} miw={24} px={6}>
                    {makeFilter.length}
                  </Badge>
                ) : null
              }
              placeholder={`${t("header.filter.make")}`}
              aria-label={`${t("header.filter.make")} - ${makeFilter.length} ${t("selected")}`}
              className={classes["multiselect-search"]}
              styles={{
                pill: { display: "none" },
                label: {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
            />
            <MultiSelect
              placeholder={t("header.filter.years")}
              data={decadeOptions}
              value={yearFilter}
              onChange={setYearFilter}
              clearable
              searchable
              c={isDark ? "white" : "black"}
              w="170px"
              leftSection={
                yearFilter.length > 0 ? (
                  <Badge variant="light" fw={500} miw={24} px={6}>
                    {yearFilter.length}
                  </Badge>
                ) : null
              }
              disabled={isDisabled}
              aria-label={`${t("header.filter.years")} - ${yearFilter.length} ${t("selected")}`}
              className={classes["multiselect-search"]}
              styles={{
                pill: { display: "none" },
                label: {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
            />
            <Box
              component="span"
              role="button"
              tabIndex={0}
              onClick={() => {
                reset();
                setFilteredResults([]);
                setSearchTerm("");
                setMakeFilter([]);
                setYearFilter([]);
              }}
              style={{
                position: "relative",
                top: 5,
                color: isDark ? "#ff8787" : "var(--mantine-color-danger-7)",
                cursor:
                  debounced.length < 1 && makeFilter.length === 0 && yearFilter.length === 0
                    ? "default"
                    : "pointer",
                display: "inline-block",
              }}
              aria-label="Cancel"
            >
              {t("header.filter.cancel_filters")}
            </Box>
          </Flex>
        </Flex>
      )}

      {isPending && (
        <Text c="dimmed" mt="sm" fz="sm">
          {t("header.loading_search")}..
        </Text>
      )}
      {error && debounced.trim().length > 0 && (
        <Text c="red" mt="sm" fz="sm">
          {t("header.error_search")}
        </Text>
      )}

      {filteredResults.length > 0 && (
        <>
          <Text size="sm" mt="lg">
            {t("header.results")}:
          </Text>
          <Box mt="md">
            {filteredResults.slice(0, 10).map((car: MarketCar) => (
              <Box key={`marketCar_${car.id}`}>
                <Anchor
                  size="sm"
                  component={Link}
                  to={paths.app.markets.car.getHref(car.id)}
                  onClick={close}
                >
                  {car.make_name} {car.model_name} {car.variant}
                </Anchor>
                <Text c="gray" fz="sm">
                  {car.production_years}
                </Text>
              </Box>
            ))}
          </Box>
        </>
      )}

      {filtersVisible && debounced && !isPending && filteredResults.length === 0 && (
        <Text c="dimmed" mt="md" size="sm">
          {t("header.no_results")}
        </Text>
      )}

      {filtersVisible && debounced.length < 1 && !isPending && filteredResults.length === 0 && (
        <Text c="dimmed" mt="md" size="sm">
          {t("header.filter.no_active_search")}
        </Text>
      )}
    </Modal>
  );
};

export default SearchModal;
